import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  List,
  Tag,
  Spin,
  message,
  Modal,
  Divider,
  Space,
  Alert
} from 'antd';
import {
  CrownOutlined,
  CheckCircleOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import './PremiumPage.scss';

const { Title, Text, Paragraph } = Typography;

const PremiumPage = observer(() => {
  const { t } = useTranslation();
  const { petStore, authStore } = useStores();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (authStore.isAuthenticated) {
      petStore.fetchPremiumStatus();
    }

    // Check for successful payment redirect
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    }
  }, [authStore.isAuthenticated, searchParams]);

  const verifyPayment = async (sessionId) => {
    setVerifying(true);
    const result = await petStore.verifyCheckoutSession(sessionId);
    setVerifying(false);

    if (result.success) {
      message.success(t('premium.subscriptionActivated'));
      navigate('/premium', { replace: true });
    } else {
      message.error(result.error || t('premium.verifyFailed'));
    }
  };

  const handleStartTrial = async () => {
    const result = await petStore.startTrial();
    if (result.success) {
      message.success(t('premium.trialStarted'));
    } else {
      message.error(result.error);
    }
  };

  const handleSubscribe = async () => {
    const result = await petStore.createSubscription({
      success_url: window.location.origin + '/premium/success',
      cancel_url: window.location.origin + '/premium'
    });

    if (result.success && result.data.checkout_url) {
      window.location.href = result.data.checkout_url;
    } else {
      message.error(result.error || t('premium.checkoutFailed'));
    }
  };

  const handleCancelSubscription = () => {
    Modal.confirm({
      title: t('premium.cancelModal.title'),
      content: t('premium.cancelModal.content'),
      okText: t('premium.cancelModal.yesCancel'),
      okType: 'danger',
      cancelText: t('premium.cancelModal.noKeep'),
      onOk: async () => {
        const result = await petStore.cancelSubscription();
        if (result.success) {
          message.success(t('premium.cancelSuccess'));
        } else {
          message.error(result.error);
        }
      }
    });
  };

  const { premiumStatus, premiumLoading } = petStore;
  const isPremium = premiumStatus?.is_premium;
  const subscription = premiumStatus?.subscription;

  if (premiumLoading || verifying) {
    return (
      <div className="premium-page loading">
        <Spin size="large" tip={verifying ? t('premium.verifying') : t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="premium-page">
      <div className="premium-header">
        <CrownOutlined className="premium-icon" />
        <Title level={2}>{t('premium.title')}</Title>
        <Paragraph className="subtitle">
          {t('premium.subtitle')}
        </Paragraph>
      </div>

      {isPremium && subscription ? (
        <Card className="subscription-status-card">
          <Row align="middle" gutter={16}>
            <Col>
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            </Col>
            <Col flex="auto">
              <Title level={4} style={{ margin: 0 }}>
                {t('premium.status.premiumMember')}
                {subscription.is_trial && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>{t('premium.status.trial')}</Tag>
                )}
              </Title>
              <Text type="secondary">
                {subscription.is_trial ? (
                  <>{t('premium.status.trialEnds', { date: subscription.trial_end_date })}</>
                ) : (
                  <>{t('premium.status.activeUntil', { date: subscription.end_date })}</>
                )}
              </Text>
              <br />
              <Text type="secondary">
                {t('premium.status.daysRemaining', { days: subscription.days_remaining })}
              </Text>
            </Col>
            <Col>
              <Space direction="vertical">
                <Button type="primary" onClick={() => navigate('/pets/calendar')}>
                  <CalendarOutlined /> {t('premium.openCalendar')}
                </Button>
                {subscription.status !== 'CANCELLED' && !subscription.is_trial && (
                  <Button danger onClick={handleCancelSubscription}>
                    {t('premium.cancelSubscription')}
                  </Button>
                )}
              </Space>
            </Col>
          </Row>

          {subscription.is_trial && (
            <Alert
              message={t('premium.enjoyingPremium.title')}
              description={t('premium.enjoyingPremium.description')}
              type="info"
              showIcon
              action={
                <Button type="primary" onClick={handleSubscribe}>
                  {t('premium.subscribeNow')}
                </Button>
              }
              style={{ marginTop: 16 }}
            />
          )}
        </Card>
      ) : (
        <>
          <Row gutter={[24, 24]} justify="center" className="pricing-cards">
            <Col xs={24} sm={16} md={12}>
              <Card className="pricing-card trial launch-trial">
                <Tag color="green" style={{ marginBottom: 16 }}>{t('premium.trial.launchOffer')}</Tag>
                <div className="pricing-header">
                  <Title level={3}>{t('premium.trial.title')}</Title>
                  <div className="price">
                    <span className="amount">0</span>
                    <span className="currency">€</span>
                    <span className="period">{t('premium.trial.period')}</span>
                  </div>
                </div>
                <Divider />
                <List
                  dataSource={[
                    t('premium.trial.feature1'),
                    t('premium.trial.feature2'),
                    t('premium.trial.feature3'),
                    t('premium.trial.feature4'),
                    t('premium.trial.feature5')
                  ]}
                  renderItem={item => (
                    <List.Item>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      {item}
                    </List.Item>
                  )}
                />
                <Alert
                  message={t('premium.trial.noAutoCharge')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16, textAlign: 'left' }}
                />
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleStartTrial}
                  loading={premiumLoading}
                  disabled={premiumStatus?.has_subscription}
                >
                  {premiumStatus?.has_subscription ? t('premium.trial.alreadyUsed') : t('premium.trial.start')}
                </Button>
              </Card>
            </Col>
          </Row>

          <div className="cancellation-policy">
            <Title level={5}>{t('premium.cancellationPolicy.title')}</Title>
            <Paragraph type="secondary">
              {t('premium.cancellationPolicy.text')}
            </Paragraph>
            <Link to="/terms">{t('premium.cancellationPolicy.viewTerms')}</Link>
          </div>
        </>
      )}

    </div>
  );
});

export default PremiumPage;
